import { useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';

interface EditRoom {
  id: string;
  room_name?: string;
  room_number?: string;
  bed_count?: number;
  room_type?: string;
  floor?: string;
  status?: string;
  notes?: string;
}

interface RoomEditModalProps {
  room: EditRoom;
  property: { id: string };
  onClose: () => void;
  onSave: () => Promise<void>;
  onDelete: () => Promise<void>;
  canEdit: boolean;
  supabase: SupabaseClient;
}

interface RoomForm {
  room_name: string;
  room_number: string;
  bed_count: number | string;
  room_type: string;
  floor: string;
  status: string;
  notes: string;
}

export default function RoomEditModal({ room, property: _property, onClose, onSave, onDelete, canEdit, supabase }: RoomEditModalProps) {
  const [form, setForm] = useState<RoomForm>({
    room_name: room.room_name || '',
    room_number: room.room_number || room.room_name || '',
    bed_count: room.bed_count || 1,
    room_type: room.room_type || '',
    floor: room.floor || '',
    status: room.status || 'active',
    notes: room.notes || ''
  });
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleSave = async (): Promise<void> => {
    if (!form.room_name.trim() && !form.room_number.trim()) {
      alert('Room name or room number is required');
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase
        .from('hosp_rooms')
        .update({
          room_name: form.room_name.trim() || null,
          room_number: form.room_number.trim() || form.room_name.trim() || null,
          bed_count: parseInt(String(form.bed_count), 10) || 1,
          room_type: form.room_type.trim() || null,
          floor: form.floor.trim() || null,
          status: form.status,
          notes: form.notes.trim() || null
        })
        .eq('id', room.id);
      if (error) throw error;
      await onSave();
    } catch (err: unknown) {
      alert('Error saving room: ' + (err instanceof Error ? err.message : String(err)));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    try {
      const { error } = await supabase
        .from('hosp_rooms')
        .delete()
        .eq('id', room.id);
      if (error) throw error;
      await onDelete();
    } catch (err: unknown) {
      alert('Error deleting room: ' + (err instanceof Error ? err.message : String(err)));
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
        <div className="modal-header">
          <h2 className="modal-title">Edit Room</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          <div className="flex flex-col gap-lg">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label className="form-label">Room Number</label>
                <input
                  type="text"
                  className="form-input"
                  value={form.room_number}
                  onChange={(e) => setForm({ ...form, room_number: e.target.value })}
                  placeholder="e.g., Room 1, 101"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Room Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={form.room_name}
                  onChange={(e) => setForm({ ...form, room_name: e.target.value })}
                  placeholder="e.g., Presidential Suite"
                />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label className="form-label">Bed Count</label>
                <input
                  type="number"
                  className="form-input"
                  value={form.bed_count}
                  onChange={(e) => setForm({ ...form, bed_count: e.target.value })}
                  min="1"
                  max="10"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Room Type</label>
                <input
                  type="text"
                  className="form-input"
                  value={form.room_type}
                  onChange={(e) => setForm({ ...form, room_type: e.target.value })}
                  placeholder="e.g., Standard, Suite"
                />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label className="form-label">Floor</label>
                <input
                  type="text"
                  className="form-input"
                  value={form.floor}
                  onChange={(e) => setForm({ ...form, floor: e.target.value })}
                  placeholder="e.g., Ground, 1st"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  className="form-input"
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea
                className="form-input"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                placeholder="Any notes about this room..."
              />
            </div>

            {canEdit && (
              <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '1rem' }}>
                {!showDeleteConfirm ? (
                  <button
                    type="button"
                    className="btn-danger-outline"
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    Delete Room
                  </button>
                ) : (
                  <div style={{ padding: '0.75rem', background: 'var(--color-error-bg)', borderRadius: 'var(--radius-md)' }}>
                    <p style={{ fontSize: 'var(--text-base)', color: 'var(--color-error-text)', marginBottom: '0.5rem' }}>
                      Delete &quot;{room.room_name}&quot;? This cannot be undone.
                    </p>
                    <div className="flex gap-sm">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setShowDeleteConfirm(false)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={handleDelete}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
